// User type for auth
export interface User {
  uid: string;
  email: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  phoneNumber?: string | null;
}
import moment from "moment";
import { DOMAINS, IMAGE_PROVIDER_DISPLAY_MAPPING } from "./constants";

/**
 * Sanitize a string for use as a safe download filename.
 * Replaces non-alphanumeric characters with underscores, collapses consecutive
 * underscores, strips leading/trailing underscores, and lowercases the result.
 */
export function sanitizeFilename(
  name: string,
  fallback: string = "file",
): string {
  let safe = name
    .replace(/[^\w\s-]/g, "_")
    .replace(/[\s_]+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase();
  return safe || fallback;
}

export function uniqueId(
  seedDate: number | string | undefined | null = new Date().getTime(),
) {
  // desired length of Id
  var idStrLen = 16;
  // always start with a letter -- base 36 makes for a nice shortcut
  var idStr = (Math.floor(Math.random() * 25) + 10).toString(36);
  // add a timestamp in milliseconds (base 36 again) as the base
  idStr += (seedDate || new Date().getTime()).toString(36);
  // similar to above, complete the Id using random, alphanumeric characters
  do {
    idStr += Math.floor(Math.random() * 35).toString(36);
  } while (idStr.length < idStrLen);

  return idStr;
}

export async function convertAudioBlobsToBase64(audioBlobs: Array<Blob>) {
  let mergedBlob = new Blob(audioBlobs, { type: audioBlobs[0].type });
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.readAsDataURL(mergedBlob);

    reader.onload = function () {
      if (reader.result && typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject();
      }
    };
  });
}

export const estimateTokens = (text: string, method = "average") => {
  // # method can be "average", "words", "chars", "max", "min", defaults to "max"
  // # "average" is the average of words and chars
  // # "words" is the word count divided by 0.75
  // # "chars" is the char count divided by 4
  // # "max" is the max of word and char\
  // # "min" is the min of word and char
  const word_count = text.split(" ").length;
  const char_count = text.length;
  const tokens_count_word_est = word_count / 0.75;
  const tokens_count_char_est = char_count / 4.0;
  var output = 0;
  if (method === "average") {
    output = (tokens_count_word_est + tokens_count_char_est) / 2;
  } else if (method === "words") {
    output = tokens_count_word_est;
  } else if (method === "chars") {
    output = tokens_count_char_est;
  } else if (method == "max") {
    output = Math.max(tokens_count_word_est, tokens_count_char_est);
  } else if (method === "min") {
    output = Math.min(tokens_count_word_est, tokens_count_char_est);
  } else {
    return (tokens_count_word_est + tokens_count_char_est) / 2;
  }
  return Math.round(output);
};

export const getUserScopedKey = (userId: string) => `activeCompanyId_${userId}`;

export const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "localhost.com" ||
  window.location.hostname === "app.revboost.ai" ||
  window.location.hostname === "127.0.0.1";

export const isDevelopment = () => {
  return isLocal || process.env.NODE_ENV === "development";
};

// Helper function to get the current domain (for whitelabel support)
export const getCurrentDomain = () => {
  return window.location.hostname;
};

// Helper function to get the appropriate API domain
export const getApiDomain = () => {
  if (isLocal) {
    // Match the frontend's host (localhost vs 127.0.0.1) so cookies set by
    // the API on this host count as same-site to the parent page — Chrome
    // otherwise blocks SameSite=Lax cookies set inside a cross-site iframe.
    return "http://localhost:5002";
  }

  const currentDomain = getCurrentDomain();
  return `https://${currentDomain}`;
};

// DEPRECATED: Use getApiDomain() instead for whitelabel support
// This export is kept for backward compatibility but now calls getApiDomain()
export const hostname = getApiDomain();

// The website chat-widget embed snippet for an agent. Single source of truth so the Agents page
// and the onboarding "deploy on your website" step render identical code, with the correct
// per-environment / whitelabel API host.
export const generateAgentEmbedCode = (agentId: string): string =>
  `<!-- AI Agent Widget -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${hostname}/static/js/widget.js';
    script.setAttribute('data-agent-id', '${agentId}');
    script.async = true;
    document.head.appendChild(script);
  })();
</script>
<!-- End AI Agent Widget -->`;

// Use ngrok URL for Twilio webhooks when running locally
// In production, use the current domain
export const twilioWebhookHostname = isLocal
  ? DOMAINS.NGROK_URL
  : `https://${window.location.hostname}`;

// Workflows hostname - uses the current domain's workflows subdomain in production
export const workflows_hostname = isLocal
  ? "http://localhost:8080"
  : `https://workflows.${window.location.hostname.replace(/^web\./, "")}`;

// Helper function to get the appropriate web domain
export const getWebDomain = () => {
  if (isLocal) {
    return DOMAINS.LOCAL_WEB;
  }

  const currentDomain = getCurrentDomain();
  return `https://${currentDomain}`;
};

// Helper function to get the OAuth callback domain
// OAuth callbacks need to go through ngrok on localhost since they come from external services
export const getOAuthCallbackDomain = () => {
  if (isLocal) {
    return DOMAINS.NGROK_URL;
  }

  const currentDomain = getCurrentDomain();
  return `https://${currentDomain}`;
};

// Helper function to get the domain for external webhook URLs
// External services (like AudienceLab) need a publicly accessible URL, not localhost
// Returns ngrok URL when local, uses current domain in production
export const getWebhookDomain = () => {
  if (isLocal) {
    return DOMAINS.NGROK_URL;
  }

  const currentDomain = getCurrentDomain();
  return `https://${currentDomain}`;
};

export const stripePublicId = isLocal
  ? "pk_test_51MuFZwGoLqtAHD3fn5EZmHPMBQRwfetyxwm8ce8qvC0mZPrpylh3yZiuEXIynkUQA2IOQz04dMGrCKwegMHx6e4A00yD7upwDF"
  : "pk_live_51MuFZwGoLqtAHD3fvr4ACaPCyhLNbSDShyQcKcwie110vUltofvfCoeORPsL35hWHntjq5xUM3ySbbZAY7PVRc1t00ykUwFb9i";

// Stripe instance caching for connected accounts (direct charges)
// This is needed because Stripe.js requires a separate instance per connected account
import { loadStripe, Stripe } from "@stripe/stripe-js";

// Default stripe promise for platform payments
let defaultStripePromise: Promise<Stripe | null> | null = null;

// Cache for connected account stripe instances
const stripeAccountCache: { [key: string]: Promise<Stripe | null> } = {};

/**
 * Get or create a Stripe instance for a connected account (for direct charges).
 *
 * For direct charge payments on Stripe Connect, the PaymentIntent is created
 * on the connected account. The frontend must use a Stripe instance initialized
 * with the connected account ID to confirm these payments.
 *
 * @param connectedAccount - The Stripe connected account ID (optional)
 * @returns Promise<Stripe | null> - A Stripe instance for the account
 */
export const getStripeForAccount = (
  connectedAccount?: string,
): Promise<Stripe | null> => {
  if (!connectedAccount) {
    // Return the default platform Stripe instance
    if (!defaultStripePromise) {
      defaultStripePromise = loadStripe(stripePublicId);
    }
    return defaultStripePromise;
  }

  // Return cached instance or create a new one for the connected account
  if (!stripeAccountCache[connectedAccount]) {
    stripeAccountCache[connectedAccount] = loadStripe(stripePublicId, {
      stripeAccount: connectedAccount,
    });
  }

  return stripeAccountCache[connectedAccount];
};

export const truncate = (input: string | undefined, maxLength = 40) => {
  if (!input) return "";
  if (input.length > maxLength) {
    return input.substring(0, maxLength) + "...";
  }
  return input;
};

export const trackAffiliate = (user: User, whitelabelSettings?: any) => {
  // Skip affiliate tracking for whitelabel instances
  if (whitelabelSettings && !whitelabelSettings.isMainSite) {
    return;
  }

  // @ts-ignore
  if (user && user.email && window.trackdesktrigger) {
    // @ts-ignore
    window.trackdesktrigger(user.email);
  }
};

export const getSupportContactInfo = (whitelabelSettings?: any): string => {
  // Only return a support URL if explicitly set by the whitelabel
  // Otherwise return empty string so the built-in support page is used
  const supportUrl = whitelabelSettings?.supportUrl;
  if (supportUrl) {
    return supportUrl;
  }
  return "";
};

export const getWhitelabelAwareErrorMessage = (
  specificError?: string,
  supportUrl?: string,
) => {
  // Use supportUrl if provided, otherwise use a generic message
  // The actual support email should come from whitelabel settings
  const contactInfo = supportUrl || "support";
  // Extract display text for the contact info
  const displayContact = contactInfo.startsWith("mailto:")
    ? contactInfo.replace("mailto:", "")
    : contactInfo;

  if (specificError) {
    return `${specificError} If you need assistance, please contact ${displayContact}.`;
  }
  return `Oops! Something went wrong! If you need assistance, please contact ${displayContact}.`;
};

export const parseError = (err: any, defaultError?: string) => {
  if (err.response) {
    if (err.response.data) {
      // Check for various error message fields
      if (err.response.data.msg) {
        return err.response.data.msg;
      }
      if (err.response.data.error) {
        return err.response.data.error;
      }
      if (err.response.data.message) {
        return err.response.data.message;
      }
    }
  }
  // For backward compatibility, use a generic error message
  return (
    defaultError ||
    "Oops! Something went wrong! If you need assistance, please contact support."
  );
};

export function countWords(str: string) {
  return str.trim().split(/\s+/).length;
}

export const fileToBase64 = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    // @ts-ignore
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const whitelabelDomains = [
  "mercenarymarketingai.netlify.app",
  "app.mercenarymarketing.ai",
];
const darkLogo = require("./img/logo_transparent_dark.png");
const logo = require("./img/logo_transparent.png");

export const getLogo = () => {
  // Return default logo - whitelabel logos should be handled through dynamic settings
  return require("./img/logo_transparent_dark.png");
};

export const getLogoMode = (mode: string) => {
  // Return appropriate logo based on mode - whitelabel logos should be handled through dynamic settings
  return mode === "dark" ? darkLogo : logo;
};

export const getInstanceConfig = (mode: string, whitelabelSettings?: any) => {
  // If whitelabel settings are provided, use them
  if (whitelabelSettings) {
    // For whitelabel domains without custom URLs, use relative paths to stay on the domain
    const whitelabelTermsUrl = whitelabelSettings.termsUrl || "/terms";
    const whitelabelPrivacyUrl = whitelabelSettings.privacyUrl || "/privacy";

    // Use dark mode colors when in dark mode, falling back to light mode colors if not set
    const primaryColor =
      mode === "dark"
        ? whitelabelSettings.primaryColorDark || whitelabelSettings.primaryColor
        : whitelabelSettings.primaryColor;
    const secondaryColor =
      mode === "dark"
        ? whitelabelSettings.secondaryColorDark ||
          whitelabelSettings.secondaryColor
        : whitelabelSettings.secondaryColor;

    return {
      ...whitelabelSettings,
      primaryColor,
      secondaryColor,
      menuColor: mode === "dark" ? "#000000" : "rgb(249, 250, 251)",
      containedTextColor: mode === "dark" ? "#000000" : "#fff",
      contrastColor: mode === "dark" ? "#fff" : "#000000",
      backgroundColor: mode === "dark" ? "#000000" : "#fff",
      termsUrl: whitelabelTermsUrl,
      privacyUrl: whitelabelPrivacyUrl,
      //paperColor: mode === "dark" ? "#31363F" : "rgb(249, 250, 251)",
    };
  }

  // Default configuration (no whitelabel settings - localhost/development only)
  // In production, whitelabel settings will be fetched from backend
  else {
    return {
      title: "GTM AI",
      headTitle: "GTM AI | Train AI on Your Business",
      faviconText: "GTM",
      primaryColor: mode === "dark" ? "#A3F7BF" : "#29A19C",
      secondaryColor: mode === "dark" ? "#fff" : "#000000",
      contrastColor: mode === "dark" ? "#fff" : "#000000",
      backgroundColor: mode === "dark" ? "#000000" : "rgb(249, 250, 251)",
      isMainSite: false,
      menuColor: mode === "dark" ? "#000000" : "rgb(249, 250, 251)",
      paperColor: mode === "dark" ? "#31363F" : "rgb(249, 250, 251)",
      containedTextColor: mode === "dark" ? "#000000" : "#fff",
      termsUrl: "/terms",
      privacyUrl: "/privacy",
    };
  }
};

export const formatTimestamp = (timestamp: any) => {
  if (!timestamp) {
    return {
      fullDateTime: "",
      date: "",
      time: "",
      dayOfWeek: "",
      timeZone: "",
      fromNow: "",
      isoString: "",
    };
  }
  // Create moment object from epoch timestamp in local timezone
  // Handle both seconds and milliseconds timestamps
  const momentDate =
    timestamp > 9999999999
      ? moment(timestamp) // milliseconds - local timezone
      : moment.unix(timestamp); // seconds - local timezone

  // Get the browser's timezone abbreviation
  const timeZone =
    momentDate.format("z") || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format the date in different ways using local timezone
  const formattedData = {
    fullDateTime: momentDate.format("MMMM Do YYYY, h:mm:ss a"),
    date: momentDate.format("YYYY-MM-DD"),
    time: momentDate.format("HH:mm:ss.SSS"),
    dayOfWeek: momentDate.format("dddd"),
    timeZone: timeZone,
    fromNow: momentDate.fromNow(), // Adding a human-readable relative time
    isoString: momentDate.toISOString(),
  };

  return formattedData;
};

/**
 * Format a date to a relative time string (e.g., "2 hours ago")
 * Accepts ISO strings or Date objects
 */
export const formatRelativeTime = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "";
  return moment(date).fromNow();
};

export const generateFaviconSVG = (text: string, color: string): string => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32">
      <rect width="32" height="32" rx="6" fill="${color}"/>
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        font-family="Arial"
        font-size="16"
        fill="#000000"
      >${text}</text>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Vintage color palette
export const vintageColors = [
  "#D8A384", // Dusty Peach
  "#8A9A5B", // Avocado Green
  "#7D7F7D", // Dusty Grey
  "#AC5F54", // Rusty Red
  "#CD8A57", // Burnt Sienna
  "#A2A641", // Olive Green
  "#9F8968", // Warm Taupe
  "#5F6E7D", // Steel Blue
  "#846C5B", // Brown Sugar
  "#4E6061", // Dark Sage
  "#B0766A", // Dusty Cedar
  "#7C8F85", // Antique Green
  "#C4AA87", // Camel
  "#B89476", // Desert Sand
  "#66818C", // Teal Blue
  "#5D4F41", // Coffee Brown
  "#A76D59", // Terracotta
  "#8E9A97", // Dusty Blue-Grey
];

// Function to get a random vintage color
export function getRandomVintageColor(): string {
  const randomIndex = Math.floor(Math.random() * vintageColors.length);
  return vintageColors[randomIndex];
}

// Function to get a consistent vintage color based on a string (for avatars, tags, etc.)
export function stringToVintageColor(str: string): string {
  if (!str) return vintageColors[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % vintageColors.length;
  return vintageColors[index];
}

// Function to get constrasting text color (black or white) based on background
export function getContrastColor(hexColor: string): string {
  // Remove the hash if it exists
  hexColor = hexColor.replace("#", "");

  // Parse the RGB components
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);

  // Calculate brightness using the luminance formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Return black for bright colors, white for dark colors
  return brightness > 150 ? "#000000" : "#FFFFFF";
}

// Prepend https:// when the user left the protocol off (e.g. "example.com").
// Leaves an existing http:// or https:// untouched.
export const withProtocol = (url: string) => {
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const validateURL = (url: string) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
    "i", // fragment locator
  );

  return !!urlPattern.test(url);
};

export const trimmedString = (string: string, length: number) => {
  return string.length > length
    ? string.substring(0, length - 3) + "..."
    : string;
};

export const formatStep = (step: any) => {
  const stepName = step.stepName || step.subject;
  if (!stepName) return "-";
  return trimmedString(`#${step.order} ${stepName}`, 40);
};

export const toAvatarName = (name: string) => {
  if (name && name.length) {
    var names = name.split(" ");
    var firstCharName = names
      .filter((x) => x)
      .map((x) => x[0])
      .join("")
      .toUpperCase();
    return firstCharName.length > 1
      ? firstCharName.substring(0, 2)
      : firstCharName[0];
  }
};

// Helper function to get the terms and privacy URLs based on whitelabel settings
export const getPolicyUrls = (whitelabelSettings?: any) => {
  const config = getInstanceConfig("light", whitelabelSettings);
  return {
    termsUrl: config.termsUrl,
    privacyUrl: config.privacyUrl,
  };
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str || str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Mirrors backend app/utils/__init__.py:toCamelCase. Smart-list headers feed
// {{templateVariables}} via this exact transformation server-side
// (_convertDictKeysToCamelCase applied to returnRowAsDict), so the frontend's
// variable picker must use the same logic — including the quirk that
// single-word inputs are lowercased ("firstName" -> "firstname").
export const toCamelCase = (name: string): string => {
  if (!name) return name;
  const normalized = name.replace(/_/g, " ").replace(/-/g, " ");
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length === 0) return name;
  let result = words[0].toLowerCase();
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    result += word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }
  return result;
};

// Helper function to get the display name for image providers
export const getImageProviderDisplayName = (provider: string): string => {
  return (
    IMAGE_PROVIDER_DISPLAY_MAPPING[provider?.toLowerCase()] ||
    provider ||
    "Unknown"
  );
};

/**
 * Add width and height query parameters to an image URL for bandwidth optimization
 * Only adds parameters if the URL supports query strings (contains http/https protocol)
 *
 * @param url - The image URL to add dimensions to
 * @param width - The desired width in pixels
 * @param height - The desired height in pixels
 * @returns The URL with width/height parameters if applicable, otherwise original URL
 */
export const addImageDimensions = (
  url: string | undefined | null,
  width?: number,
  height?: number,
): string | undefined => {
  if (!url) return undefined;

  // Only add query params if the URL is a full URL (http/https)
  // Don't modify data URLs, relative paths, or other protocols
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return url;
  }

  // If no dimensions provided, return original URL
  if (!width && !height) {
    return url;
  }

  try {
    const urlObj = new URL(url);

    if (width) {
      urlObj.searchParams.set("width", width.toString());
    }
    if (height) {
      urlObj.searchParams.set("height", height.toString());
    }

    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, return original URL
    console.warn("Failed to add image dimensions to URL:", url, error);
    return url;
  }
};

/**
 * Get session metadata from the browser for AI context.
 * Based on ChatGPT's Layer 1: Session Metadata.
 */
export const getSessionMetadata = (): {
  device: string;
  timezone: string;
  location: string;
} => {
  // Detect device type
  const userAgent = navigator.userAgent;
  let device = "Desktop";
  if (/Mobi|Android/i.test(userAgent)) {
    device = "Mobile";
  } else if (/Tablet|iPad/i.test(userAgent)) {
    device = "Tablet";
  }

  // Get timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // Get approximate location from timezone (e.g., "America/New_York" -> "New York")
  const location = timezone.split("/").pop()?.replace(/_/g, " ") || "Unknown";

  return {
    device,
    timezone,
    location,
  };
};

export const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
};
